import React, { useEffect, useRef, useState } from 'react';
import { NodeProps, NodeResizer, useStoreApi } from 'reactflow';
import { useBoardMultiSelected } from '../useBoardSelection';
import type { BoardNoteData, CommentMention } from '../../../types';
import { DEFAULT_NOTE_COLOR, DEFAULT_BOARD_NOTE_FONT_SIZE } from '../boardConstants';
import { useBoardActions } from '../boardActions';
import NodeActionsToolbar from './NodeActionsToolbar';
import BoardNoteEditor from './BoardNoteEditor';
import RichCommentRenderer from '../../pm/RichCommentRenderer';
import { useBoardComments } from '../comments/BoardCommentsContext';
import { useAuth } from '../../../utils/AuthContext';
import { useOptionalCRM } from '../../../utils/CRMContext';
import { notifyBoardMentions } from '../../../utils/mentionNotification';
import { initialNoteEditing, shouldKeepNoteEditing } from './noteEditing';

const NoteNode: React.FC<NodeProps<BoardNoteData>> = ({ id, data, selected, dragging }) => {
  const { updateNodeData, readOnly } = useBoardActions();
  const store = useStoreApi();
  const { mentionableUsers, dealId } = useBoardComments();
  const { user } = useAuth();
  const crm = useOptionalCRM();
  const enhancedDeals = crm?.enhancedDeals ?? [];
  const timberProjects = crm?.timberProjects ?? [];
  // Only auto-edit when a brand-new empty note mounts already selected (Add note).
  // Selecting an existing note must stay non-editing so React Flow can drag it —
  // TipTap surfaces use `nodrag`, which otherwise blocks move (CRM #28 / #31).
  const [editing, setEditing] = useState(() =>
    initialNoteEditing({
      selected,
      readOnly,
      text: data.text,
      textHtml: data.textHtml,
    }),
  );
  const multiSelected = useBoardMultiSelected();
  const fontSize = data.fontSize || DEFAULT_BOARD_NOTE_FONT_SIZE;
  const mentionsOnSelect = useRef<string[]>([]);
  const wasSelectedRef = useRef(false);
  const mentionsRef = useRef<CommentMention[]>(data.mentions || []);
  mentionsRef.current = data.mentions || [];

  useEffect(() => {
    if (!shouldKeepNoteEditing({ selected, multiSelected, dragging })) setEditing(false);
  }, [selected, multiSelected, dragging]);

  useEffect(() => {
    if (selected) {
      if (!wasSelectedRef.current) {
        mentionsOnSelect.current = (data.mentions || []).map((m) => m.userId);
      }
      wasSelectedRef.current = true;
      return;
    }
    if (!wasSelectedRef.current || readOnly) {
      wasSelectedRef.current = false;
      return;
    }
    wasSelectedRef.current = false;
    const deal = enhancedDeals.find((d) => d.id === dealId);
    const timber = timberProjects.find((p) => p.dealId === dealId);
    const previous = new Set(mentionsOnSelect.current);
    const added = (mentionsRef.current || []).filter(
      (m) => m.userId && !previous.has(m.userId) && m.userId !== user?.id,
    );
    if (added.length === 0 || !deal?.leadId) return;
    void notifyBoardMentions({
      mentionedUserIds: added.map((m) => m.userId),
      fromUserId: user?.id || '',
      fromUserName: user?.name || user?.email || 'Team member',
      leadId: deal.leadId,
      dealId,
      projectId: timber?.id,
      projectName: deal.projectName || deal.title || 'the inspiration board',
      nodeId: id,
    });
  }, [selected, readOnly, dealId, enhancedDeals, timberProjects, user, id]);

  const handleRichChange = (payload: { textHtml: string; text: string; mentions: CommentMention[] }) => {
    updateNodeData(id, payload);
  };

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly}
        minWidth={120}
        minHeight={100}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar id={id} showColors showFontSize fontSize={fontSize} />
      <div
        className={`board-node board-note-node ${selected ? 'is-selected' : ''} ${editing ? 'is-editing' : ''}`}
        style={{ background: data.color || DEFAULT_NOTE_COLOR }}
        onDoubleClick={() => {
          if (readOnly) return;
          store.getState().addSelectedNodes([id]);
          setEditing(true);
        }}
      >
        {/* Drag chrome: stays free of `nodrag` so notes can move even while TipTap is open. */}
        {!readOnly && (
          <div className="board-note-drag-handle" title="Drag to move" aria-hidden />
        )}
        {editing && !readOnly ? (
          <div className="board-note-editor-wrap">
            <BoardNoteEditor
              initialHtml={data.textHtml}
              initialPlain={data.text}
              fontSize={fontSize}
              mentionableUsers={mentionableUsers}
              onChange={handleRichChange}
              autoFocus
            />
          </div>
        ) : (
          <div className="board-note-text" style={{ fontSize }}>
            {data.textHtml || data.text ? (
              <RichCommentRenderer
                bodyHtml={data.textHtml}
                body={data.text || ''}
                className="board-note-rich"
              />
            ) : (
              <span className="board-node-placeholder">Double-click to edit</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(NoteNode);
